import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Logo from '../../components/brand/Logo.jsx'
import Icon from '../../components/ui/Icon.jsx'
import Button from '../../components/ui/Button.jsx'
import LanguageSwitch from '../../components/ui/LanguageSwitch.jsx'
import { Field, Input, Textarea } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'

/*
 * Public landing — DESIGN.md §A. Translucent navbar, hero + app mockup, feature
 * grid, coming-soon, pricing (base + pro + volumetric), contact, about, violet
 * footer. Copy from the prototype; companies named there are fictional.
 */
const arr = (x) => (Array.isArray(x) ? x : [])
const FEATURE_ICONS = ['article', 'send', 'note', 'invoice', 'mail', 'link']

export default function LandingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <Navbar t={t} navigate={navigate} scrollTo={scrollTo} />
      <Hero t={t} navigate={navigate} scrollTo={scrollTo} />
      <Features t={t} />
      <Pricing t={t} navigate={navigate} />
      <Contact t={t} navigate={navigate} />
      <About t={t} />
      <Footer t={t} />
    </div>
  )
}

function Navbar({ t, navigate, scrollTo }) {
  return (
    <header style={{ height: 72, background: 'rgba(255,255,255,.85)', backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 38px', position: 'sticky', top: 0, zIndex: 50 }}>
      <Logo size={27} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
      <nav style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {[['pricing', 'lp-pricing'], ['contact', 'lp-contact'], ['about', 'lp-about']].map(([k, id]) => (
          <button key={k} onClick={() => scrollTo(id)} style={navLink}>{t(`landing.nav.${k}`)}</button>
        ))}
      </nav>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <LanguageSwitch />
        <button onClick={() => navigate('/login')} style={{ ...navLink, fontWeight: 600 }}>{t('landing.nav.login')}</button>
        <Button variant="primary" onClick={() => navigate('/register')}>{t('landing.nav.signup')}</Button>
      </div>
    </header>
  )
}

function Hero({ t, navigate, scrollTo }) {
  return (
    <section style={{ maxWidth: 1180, margin: '0 auto', padding: '70px 38px 40px', display: 'grid', gridTemplateColumns: '1.1fr .9fr', gap: 48, alignItems: 'center' }}>
      <div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 999, background: 'var(--color-info-bg)', color: 'var(--color-info)', fontSize: 12.5, fontWeight: 600, marginBottom: 22 }}>
          <Icon name="check" size={15} /> {t('landing.hero.badge')}
        </span>
        <h1 style={{ fontFamily: 'var(--font-title)', fontSize: 50, lineHeight: 1.06, fontWeight: 800, color: 'var(--color-ink)', letterSpacing: '-.02em', marginBottom: 20 }}>{t('landing.hero.title')}</h1>
        <p style={{ fontSize: 17, color: 'var(--color-text)', lineHeight: 1.6, marginBottom: 18 }}>{t('landing.hero.subtitle')}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', background: 'var(--color-warning-bg)', color: 'var(--color-warning)', borderRadius: 12, fontSize: 13, marginBottom: 26 }}>
          <Icon name="info" size={17} /> {t('landing.hero.note')}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button variant="primary" icon="plus" onClick={() => navigate('/register')}>{t('landing.hero.ctaPrimary')}</Button>
          <Button variant="secondary" icon="credit-card" onClick={() => scrollTo('lp-pricing')}>{t('landing.hero.ctaSecondary')}</Button>
        </div>
      </div>
      <AppMockup />
    </section>
  )
}

function AppMockup() {
  // Fictional demo data (per DESIGN.md — companies on public pages are fictional).
  const rows = [['Delcourt & Fils SRL', 'Payée', 'success'], ['Voltéo Énergie', 'Reçue', 'info'], ['Maxiprix NV', 'En retard', 'warning']]
  return (
    <div style={{ background: 'linear-gradient(180deg,#2C1C4E,#211339)', borderRadius: 20, padding: 18, boxShadow: 'var(--shadow-pop)' }}>
      <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border-row)' }}><Logo size={18} /></div>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
            {[['110', 'Total'], ['94', 'Entrantes'], ['16', 'Envoyées']].map(([v, l]) => (
              <div key={l} style={{ background: 'var(--color-bg-lilac)', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 20, color: 'var(--color-ink)' }}>{v}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{l}</div>
              </div>
            ))}
          </div>
          {rows.map(([name, st, tone]) => (
            <div key={name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderTop: '1px solid var(--color-border-row)' }}>
              <span style={{ fontSize: 13, color: 'var(--color-ink)' }}>{name}</span>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, color: `var(--color-${tone})`, background: `var(--color-${tone}-bg)` }}>{st}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function Features({ t }) {
  const items = arr(t('landing.features.items', { returnObjects: true }))
  const soon = arr(t('landing.soon.items', { returnObjects: true }))
  return (
    <section id="lp-features" style={{ background: 'var(--color-bg-page)', padding: '64px 38px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <SectionHead title={t('landing.features.title')} subtitle={t('landing.features.subtitle')} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          {items.map((f, i) => (
            <div key={f.t} style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 16, padding: 22, boxShadow: 'var(--shadow-card)' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--color-bg-lilac-strong)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}><Icon name={FEATURE_ICONS[i] || 'check'} size={22} /></div>
              <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16, color: 'var(--color-ink)', marginBottom: 6 }}>{f.t}</h3>
              <p style={{ fontSize: 14, color: 'var(--color-text)', lineHeight: 1.5 }}>{f.d}</p>
            </div>
          ))}
        </div>
        {soon.length > 0 && (
          <>
            <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, color: 'var(--color-ink)', margin: '40px 0 18px' }}>{t('landing.soon.title')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 18 }}>
              {soon.map((f) => (
                <div key={f.t} style={{ background: 'var(--color-bg-lilac)', border: '1px dashed var(--color-border-strong)', borderRadius: 16, padding: 20 }}>
                  <h4 style={{ fontWeight: 700, color: 'var(--color-ink)', marginBottom: 5 }}>{f.t}</h4>
                  <p style={{ fontSize: 14, color: 'var(--color-text)' }}>{f.d}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

function Pricing({ t, navigate }) {
  // Real plans from the DB (same source the registration wizard uses) — no
  // invented tiers. Falls back gracefully to the static copy if the call fails.
  const [plans, setPlans] = useState(null)
  useEffect(() => {
    api.get('/billing/plans/public').then(({ data }) => setPlans((data?.plans || []).filter((p) => p.is_active !== false && p.is_visible !== false))).catch(() => setPlans([]))
  }, [])

  const fmt = (n) => new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(n || 0)
  const features = arr(t('landing.pricing.features', { returnObjects: true }))
  const single = plans && plans.length === 1
  const lead = plans?.[0]

  return (
    <section id="lp-pricing" style={{ padding: '64px 38px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <SectionHead title={t('landing.pricing.title')} subtitle={t('landing.pricing.subtitle')} />
        {plans === null ? (
          <div style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: single ? 'minmax(320px, 520px)' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18, justifyContent: 'center' }}>
              {plans.map((p, i) => (
                <div key={p.id} style={{ background: '#fff', border: '1.5px solid ' + (i === 0 ? 'var(--color-primary)' : 'var(--color-border)'), borderRadius: 18, padding: 28, boxShadow: 'var(--shadow-card)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--color-primary)', marginBottom: 10 }}>{p.name}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 38, color: 'var(--color-ink)' }}>{p.is_free ? t('register.free') : fmt(p.monthly_base_price)}</span>
                    {!p.is_free && <span style={{ color: 'var(--color-text)', fontSize: 14 }}>{t('landing.pricing.period')} · {t('subscription.htva')}</span>}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '6px 0 18px' }}>{p.description || t('landing.pricing.note')}</p>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10 }}>{t('landing.pricing.includes')}</div>
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 22 }}>
                    <li style={{ display: 'flex', gap: 9, fontSize: 14, color: 'var(--color-text)' }}><span style={{ color: 'var(--color-success)' }}><Icon name="check" size={17} /></span>{t('landing.pricing.storage', { count: p.included_stored_invoices })}</li>
                    {features.map((feat) => <li key={feat} style={{ display: 'flex', gap: 9, fontSize: 14, color: 'var(--color-text)' }}><span style={{ color: 'var(--color-success)' }}><Icon name="check" size={17} /></span>{feat}</li>)}
                  </ul>
                  <Button variant={i === 0 ? 'primary' : 'secondary'} fullWidth onClick={() => navigate('/register')}>{t('landing.pricing.cta')}</Button>
                </div>
              ))}
            </div>
            {lead && (lead.price_per_outgoing_invoice || lead.price_per_incoming_invoice) && (
              <div style={{ background: 'var(--color-bg-lilac-strong)', borderRadius: 16, padding: 24, marginTop: 18, textAlign: 'center' }}>
                <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 17, color: 'var(--color-ink)', marginBottom: 4 }}>+ {t('landing.pricing.volume.title')}</h3>
                <p style={{ color: 'var(--color-text)', fontSize: 14, marginBottom: 12 }}>{t('landing.pricing.volume.subtitle')}</p>
                <div style={{ display: 'flex', gap: 22, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Vol price={fmt(lead.price_per_outgoing_invoice)} label={t('landing.pricing.volume.outgoing')} />
                  <Vol price={fmt(lead.price_per_incoming_invoice)} label={t('landing.pricing.volume.incoming')} />
                  {lead.price_per_100_extra_invoices ? <Vol price={fmt(lead.price_per_100_extra_invoices)} label={t('landing.pricing.volume.extra')} /> : null}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}

function Vol({ price, label }) {
  return (
    <span style={{ fontSize: 14, color: 'var(--color-ink)' }}>
      <strong style={{ fontFamily: 'var(--font-title)', fontWeight: 800 }}>{price}</strong> {label}
    </span>
  )
}

function Contact({ t, navigate }) {
  const { toast } = useFeedback()
  const [f, setF] = useState({ name: '', email: '', company: '', phone: '', message: '' })
  const [sending, setSending] = useState(false)
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  const submit = async (e) => {
    e.preventDefault()
    setSending(true)
    try { await api.post('/contact', f); toast(t('landing.contact.success'), 'success'); setF({ name: '', email: '', company: '', phone: '', message: '' }) }
    catch { toast(t('landing.contact.error'), 'error') } finally { setSending(false) }
  }
  return (
    <section id="lp-contact" style={{ background: 'var(--color-bg-page)', padding: '64px 38px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 40, alignItems: 'start' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 30, color: 'var(--color-ink)', marginBottom: 8 }}>{t('landing.contact.title')}</h2>
          <p style={{ color: 'var(--color-text)', fontSize: 15, marginBottom: 26 }}>{t('landing.contact.subtitle')}</p>
          <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 16, padding: 22, boxShadow: 'var(--shadow-card)' }}>
            <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16, color: 'var(--color-ink)', marginBottom: 6 }}>{t('landing.contact.onlineTitle')}</h3>
            <p style={{ color: 'var(--color-text)', fontSize: 14, marginBottom: 16 }}>{t('landing.contact.onlineText')}</p>
            <Button variant="primary" icon="plus" onClick={() => navigate('/register')}>{t('landing.contact.onlineCta')}</Button>
          </div>
        </div>
        <form onSubmit={submit} style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 16, padding: 24, boxShadow: 'var(--shadow-card)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label={t('landing.contact.name')} required><Input value={f.name} onChange={(e) => set('name', e.target.value)} required /></Field>
            <Field label={t('landing.contact.email')} required><Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} required /></Field>
            <Field label={t('landing.contact.company')}><Input value={f.company} onChange={(e) => set('company', e.target.value)} /></Field>
            <Field label={t('landing.contact.phone')}><Input value={f.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
          </div>
          <Field label={t('landing.contact.message')}><Textarea rows={4} value={f.message} onChange={(e) => set('message', e.target.value)} /></Field>
          <Button type="submit" variant="primary" icon="send" loading={sending}>{t('landing.contact.submit')}</Button>
        </form>
      </div>
    </section>
  )
}

function About({ t }) {
  const stats = arr(t('landing.about.stats', { returnObjects: true }))
  return (
    <section id="lp-about" style={{ padding: '64px 38px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.2fr .8fr', gap: 44, alignItems: 'center' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 30, color: 'var(--color-ink)', marginBottom: 16 }}>{t('landing.about.title')}</h2>
          {['p1', 'p2', 'p3'].map((p) => <p key={p} style={{ color: 'var(--color-text)', fontSize: 15, lineHeight: 1.65, marginBottom: 12 }}>{t(`landing.about.${p}`)}</p>)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {stats.map((s) => (
            <div key={s.l} style={{ background: 'var(--color-bg-lilac)', borderRadius: 16, padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 26, color: 'var(--color-primary)' }}>{s.v}</span>
              <span style={{ fontSize: 14, color: 'var(--color-ink)', fontWeight: 500 }}>{s.l}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Footer({ t }) {
  return (
    <footer style={{ background: 'linear-gradient(180deg,#2C1C4E,#211339)', color: '#fff', padding: '40px 38px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <Logo size={24} dark />
          <p style={{ color: 'rgba(255,255,255,.6)', fontSize: 13.5, marginTop: 8, maxWidth: 360 }}>{t('landing.footer.tagline')}</p>
        </div>
        <div style={{ display: 'flex', gap: 22, fontSize: 13.5 }}>
          <Link to="/legal/cgu" style={{ color: 'rgba(255,255,255,.75)' }}>CGU</Link>
          <Link to="/legal/privacy" style={{ color: 'rgba(255,255,255,.75)' }}>Confidentialité</Link>
        </div>
      </div>
      <div style={{ maxWidth: 1180, margin: '22px auto 0', borderTop: '1px solid rgba(255,255,255,.1)', paddingTop: 16, color: 'rgba(255,255,255,.5)', fontSize: 12.5 }}>
        © {2026} Invoo · Espix SRL — {t('landing.footer.rights')}
      </div>
    </footer>
  )
}

const navLink = { fontSize: 14, fontWeight: 500, color: 'var(--color-text)', padding: '8px 14px', borderRadius: 9, cursor: 'pointer', background: 'none', border: 'none' }

function SectionHead({ title, subtitle }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 36 }}>
      <h2 style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 30, color: 'var(--color-ink)', marginBottom: 8 }}>{title}</h2>
      <p style={{ color: 'var(--color-text)', fontSize: 15 }}>{subtitle}</p>
    </div>
  )
}
